import { useLazyQuery } from "@apollo/react-hooks";
import {
  Button,
  Checkbox,
  ChoiceList,
  Spinner,
  Stack,
  TextField,
} from "@shopify/polaris";
import React from "react";
import { GET_PRINTIFY_AVAIABLE_SHOPS } from "../../../graphql/queries";
import { handleError } from "../../../helper";
import { ComponentLabelPolaris } from "../../shared/ComponentLabelPolaris";

const errMsg = {
  apiKey: "Store URL is required",
  apiSecret: "Access token required.",
};

function PrintifyFCImpl({ apiKey, printifyAutoProduction }, forwardedRef) {
  const [state, setState] = React.useReducer((p, s) => ({ ...p, ...s }), {
    apiKey: "",
    apiSecret: "",
    error: {},
    getStore: false,
    stores: [],
    selectedStore: [],
    disabledBtn: true,
    loading: false,
    printifyAutoProduction: !!printifyAutoProduction,
  });

  // Query
  const [
    getStores,
    { data: dataShop, loading: loadingShop, error: errorShop },
  ] = useLazyQuery(GET_PRINTIFY_AVAIABLE_SHOPS);

  // Effects
  React.useEffect(() => {
    const nodes = dataShop?.getPrintifyAvaiableShops || [];
    if (nodes?.length > 0) {
      const nodesFiltered = nodes
        // .filter(({ salesChannel }) => salesChannel === CUSTOM_INTEGRATION)
        .map(({ id, title }) => ({ label: title, value: id }));
      setState({ stores: nodesFiltered, disabledBtn: true });
    }
  }, [dataShop]);

  const handleValidate = React.useCallback(
    (value, id, prevError) => {
      let err = "";
      if (!value) {
        err = errMsg[id];
      }
      const newError = { ...prevError, [id]: err };
      setState({ error: newError });
      return newError;
    },
    [state.error],
  );

  const handleChange = React.useCallback((value, id) => {
    setState({ [id]: value, getStore: false });
  }, []);

  const handleSubmit = React.useCallback(() => {
    handleValidate(state.apiKey, "apiKey");
    const [storeID] = state.selectedStore || [];
    if (state.apiKey && storeID) {
      return {
        apiKey: state.apiKey,
        description: state.description,
        storeID: storeID + "",
        printifyAutoProduction: state.printifyAutoProduction,
      };
    }
  }, [
    state.apiKey,
    state.apiSecret,
    state.error,
    state.description,
    state.printifyAutoProduction,
    handleValidate,
  ]);

  const handleStoreChange = React.useCallback((value) => {
    setState({ selectedStore: value });
  }, []);

  const handleAutoProductionChange = React.useCallback((value) => {
    setState({ printifyAutoProduction: value });
  }, []);

  const handleGetStore = React.useCallback(() => {
    if (!state.apiKey) return;
    setState({ getStore: true });
    getStores({
      variables: {
        apiKey: state.apiKey,
      },
    });
  }, [getStores, state.apiKey]);

  React.useImperativeHandle(forwardedRef, () => ({ onSubmit: handleSubmit }));

  return !apiKey ? (
    <Stack vertical spacing="tight">
      <div>
        <ComponentLabelPolaris label="Api Key" required />
        <TextField
          value={state.apiKey}
          onChange={handleChange}
          placeholder="Enter API Key"
          id="apiKey"
        />
      </div>
      <TextField
        value={state.description}
        onChange={handleChange}
        placeholder="Enter description"
        id="description"
        label="Description"
      />
      <Checkbox
        label="Auto send orders to In Production"
        onChange={handleAutoProductionChange}
        checked={state.printifyAutoProduction}
      />
      <Button
        children="Get stores"
        onClick={handleGetStore}
        size="slim"
        disabled={!state.apiKey}
      />
      <div>
        {loadingShop ? (
          <Spinner size="small" />
        ) : errorShop ? (
          <div>{handleError(errorShop.toString())}</div>
        ) : dataShop ? (
          <div>
            <ComponentLabelPolaris label="Stores" required />
            <ChoiceList
              title="Stores"
              titleHidden
              onChange={handleStoreChange}
              selected={state.selectedStore}
              choices={state.stores}
            />
          </div>
        ) : null}
      </div>
    </Stack>
  ) : null;
}

const PrintifyFC = React.forwardRef(PrintifyFCImpl);
export default PrintifyFC;
